import React, { useState } from 'react';
import prestacaoService from '../../services/prestacaoContaService';
import type { PrestacaoPc, PrestacaoPcDecisao, PrestacaoPcSituacao } from '../../services/prestacaoContaService';

export const SITUACAO_PC_LABEL: Record<PrestacaoPcSituacao, string> = {
  PENDENTE: 'Pendente',
  RASCUNHO: 'Rascunho',
  EM_ANALISE: 'Em análise',
  APROVADO: 'Aprovado',
  APROVADO_COM_RESSALVA: 'Aprovado com ressalva',
  EM_CORRECAO: 'Em correção',
  REPROVADO: 'Reprovado',
};

const SITUACAO_PC_CLASS: Record<PrestacaoPcSituacao, string> = {
  PENDENTE: 'bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700',
  RASCUNHO: 'bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-500/30',
  EM_ANALISE: 'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/30',
  APROVADO: 'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/30',
  APROVADO_COM_RESSALVA: 'bg-teal-50 text-teal-700 ring-teal-200 dark:bg-teal-500/10 dark:text-teal-300 dark:ring-teal-500/30',
  EM_CORRECAO: 'bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/30',
  REPROVADO: 'bg-red-50 text-red-700 ring-red-200 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/30',
};

export const SituacaoPcBadge: React.FC<{ situacao: PrestacaoPcSituacao }> = ({ situacao }) => (
  <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${SITUACAO_PC_CLASS[situacao] ?? SITUACAO_PC_CLASS.PENDENTE}`}>
    {SITUACAO_PC_LABEL[situacao] ?? situacao}
  </span>
);

const DECISOES: { value: PrestacaoPcDecisao; label: string; exigeParecer: boolean }[] = [
  { value: 'APROVAR', label: 'Aprovar', exigeParecer: false },
  { value: 'APROVAR_COM_RESSALVA', label: 'Aprovar com ressalva', exigeParecer: true },
  { value: 'SOLICITAR_CORRECAO', label: 'Solicitar correção', exigeParecer: true },
  { value: 'REPROVAR', label: 'Reprovar', exigeParecer: true },
];

type Props = {
  pc: PrestacaoPc;
  planoId: string;
  onChanged: () => void | Promise<void>;
};

/** Envio para análise (instituição) e decisão (Convênios). As flags vêm prontas do backend. */
const PrestacaoPcAprovacaoPanel: React.FC<Props> = ({ pc, planoId, onChanged }) => {
  const [decisao, setDecisao] = useState<PrestacaoPcDecisao | ''>('');
  const [parecer, setParecer] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const executar = async (acao: () => Promise<unknown>) => {
    setSalvando(true);
    setErro(null);
    try {
      await acao();
      setDecisao('');
      setParecer('');
      await onChanged();
    } catch (e) {
      setErro((e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Não foi possível concluir a ação.');
    } finally {
      setSalvando(false);
    }
  };

  const opcao = DECISOES.find((d) => d.value === decisao);
  const decisaoValida = !!opcao && (!opcao.exigeParecer || parecer.trim().length > 0);
  const aguardandoEnvio = pc.situacao === 'RASCUNHO' || pc.situacao === 'EM_CORRECAO';
  const mostrarEnvio = aguardandoEnvio && (pc.podeEnviarParaAnalise || pc.pendenciasEnvio.length > 0);

  if (!mostrarEnvio && !pc.podeDecidir && !pc.decisaoParecer && pc.situacao !== 'EM_CORRECAO') return null;

  return (
    <div className="mx-5 mt-4 space-y-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs dark:border-slate-700 dark:bg-slate-800/50">
      {pc.situacao === 'EM_CORRECAO' && pc.decisaoParecer && (
        <div>
          <p className="font-semibold text-rose-700 dark:text-rose-300">
            Correção solicitada ({pc.correcoesSolicitadas} de {pc.limiteCorrecoes})
          </p>
          <p className="mt-1 whitespace-pre-wrap text-slate-700 dark:text-slate-200">{pc.decisaoParecer}</p>
        </div>
      )}

      {pc.situacao !== 'EM_CORRECAO' && pc.decisaoParecer && (
        <div>
          <p className="font-semibold text-slate-800 dark:text-slate-100">Parecer da decisão{pc.decisaoPor ? ` (${pc.decisaoPor})` : ''}</p>
          <p className="mt-1 whitespace-pre-wrap text-slate-700 dark:text-slate-200">{pc.decisaoParecer}</p>
        </div>
      )}

      {mostrarEnvio && (
        <div className="space-y-2">
          {pc.pendenciasEnvio.length > 0 && (
            <div>
              <p className="font-semibold text-amber-700 dark:text-amber-300">Para enviar à análise, falta:</p>
              <ul className="mt-1 list-disc pl-5 text-slate-700 dark:text-slate-200">
                {pc.pendenciasEnvio.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </div>
          )}
          {pc.podeEnviarParaAnalise && (
            <button
              type="button"
              disabled={salvando}
              onClick={() => executar(() => prestacaoService.enviarParaAnalise(pc.pcId, planoId))}
              className="rounded-lg bg-emerald-600 px-4 py-2 font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-50"
            >
              {salvando ? 'Enviando...' : 'Enviar para análise'}
            </button>
          )}
          {pc.podeEnviarParaAnalise && (
            <p className="text-slate-500 dark:text-slate-400">Depois de enviada, a PC fica travada até a decisão de Convênios.</p>
          )}
        </div>
      )}

      {pc.podeDecidir && (
        <div className="space-y-2">
          <p className="font-semibold text-slate-800 dark:text-slate-100">Decisão sobre a PC</p>
          <div className="flex flex-wrap gap-2">
            {DECISOES.map((d) => (
              <button
                key={d.value}
                type="button"
                disabled={d.value === 'SOLICITAR_CORRECAO' && !pc.podeSolicitarCorrecao}
                title={
                  d.value === 'SOLICITAR_CORRECAO' && !pc.podeSolicitarCorrecao
                    ? 'Limite de correções atingido ou prazo da PC expirado'
                    : undefined
                }
                onClick={() => setDecisao(d.value)}
                className={`rounded-lg border px-3 py-1.5 font-semibold transition disabled:cursor-not-allowed disabled:opacity-40 ${
                  decisao === d.value
                    ? 'border-blue-600 bg-blue-600 text-white'
                    : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200'
                }`}
              >
                {d.label}
              </button>
            ))}
          </div>
          {opcao && (
            <>
              <textarea
                rows={3}
                value={parecer}
                onChange={(e) => setParecer(e.target.value)}
                placeholder={opcao.exigeParecer ? 'Parecer (obrigatório)' : 'Parecer (opcional)'}
                className="w-full rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-700 outline-none focus:border-blue-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
              />
              {decisao === 'REPROVAR' && (
                <p className="text-slate-500 dark:text-slate-400">
                  A devolução do recurso à prefeitura ainda não é tratada pelo sistema.
                </p>
              )}
              <button
                type="button"
                disabled={!decisaoValida || salvando}
                onClick={() => executar(() => prestacaoService.decidirPc(pc.pcId, planoId, decisao as PrestacaoPcDecisao, parecer.trim()))}
                className="rounded-lg bg-blue-600 px-4 py-2 font-semibold text-white transition hover:bg-blue-700 disabled:opacity-50"
              >
                {salvando ? 'Salvando...' : `Confirmar: ${opcao.label.toLowerCase()}`}
              </button>
            </>
          )}
          <p className="text-slate-500 dark:text-slate-400">
            Correções solicitadas: {pc.correcoesSolicitadas} de {pc.limiteCorrecoes}.
          </p>
        </div>
      )}

      {erro && <p className="text-red-600">{erro}</p>}
    </div>
  );
};

export default PrestacaoPcAprovacaoPanel;
